<template>
  <div ref="rootRef" class="text-curve">
    <slot />
  </div>
</template>

<script setup>
import gsap from 'gsap'
import SplitText from 'gsap/SplitText'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

gsap.registerPlugin(SplitText)

const rootRef = ref(null)
const CURVE_STRENGTH = .6

let lineSplit = null
let charSplit = null
let resizeFrame = null

function curveLines() {
  const lines = lineSplit?.lines || []
  const lineMetrics = lines.map((line) => {
    const chars = [...line.querySelectorAll('.text-curve__char')]

    const metrics = chars.map((char) => ({
      el: char,
      left: char.offsetLeft,
      width: char.offsetWidth,
      height: char.offsetHeight,
    }))

    const minLeft = metrics.length > 0
      ? Math.min(...metrics.map(({ left }) => left))
      : 0
    const totalWidth = metrics.length > 0
      ? Math.max(...metrics.map(({ left, width }) => left + width)) - minLeft
      : 0

    return {
      line,
      metrics,
      minLeft,
      totalWidth,
    }
  })

  const widestLine = Math.max(...lineMetrics.map(({ totalWidth }) => totalWidth), 0)
  const radius = Math.max(widestLine * (1.2 + (1 - CURVE_STRENGTH) * 0.95), 320)
  const maxAngle = (Math.PI / 2.9) * CURVE_STRENGTH
  const sharedCenterX = widestLine / 2
  const maxCurveY = radius - Math.cos(maxAngle / 2) * radius
  const tallestChar = Math.max(
    0,
    ...lineMetrics.flatMap(({ metrics }) => metrics.map(({ height }) => height))
  )
  const sharedLineHeight = maxCurveY + tallestChar

  lineMetrics.forEach(({ line, metrics, minLeft, totalWidth }) => {
    const lineOffset = (widestLine - totalWidth) / 2

    gsap.set(line, {
      position: 'relative',
      display: 'block',
      width: widestLine,
      marginInline: 'auto',
    })

    metrics.forEach(({ el, left, width }) => {
      const normalizedLeft = left - minLeft
      const sharedLeft = lineOffset + normalizedLeft
      const charCenter = sharedLeft + width / 2
      const normalized = sharedCenterX === 0 ? 0 : (charCenter - sharedCenterX) / sharedCenterX
      const angle = normalized * (maxAngle / 2)
      const y = radius - Math.cos(angle) * radius

      gsap.set(el, {
        position: 'absolute',
        left: sharedLeft,
        top: y,
        rotation: angle * (180 / Math.PI),
        transformOrigin: '50% 100%',
      })
    })

    gsap.set(line, {
      height: sharedLineHeight,
    })
  })
}

function splitText() {
  const target = rootRef.value?.firstElementChild

  if (!target) {
    return
  }

  lineSplit?.revert()
  charSplit?.revert()

  lineSplit = SplitText.create(target, {
    type: 'lines',
    linesClass: 'text-curve__line',
  })

  charSplit = SplitText.create(lineSplit.lines, {
    type: 'chars',
    charsClass: 'text-curve__char',
  })

  curveLines()
}

function scheduleSplitText() {
  if (resizeFrame) {
    cancelAnimationFrame(resizeFrame)
  }

  resizeFrame = requestAnimationFrame(async () => {
    resizeFrame = null
    await nextTick()
    splitText()
  })
}

onMounted(async () => {
  await nextTick()
  splitText()
  window.addEventListener('resize', scheduleSplitText)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', scheduleSplitText)

  if (resizeFrame) {
    cancelAnimationFrame(resizeFrame)
  }

  charSplit?.revert()
  lineSplit?.revert()
})
</script>
